import { t } from "adapters/i18n";
import type { Goal } from "shared/types";
import type { DataResult, ServiceConstructor } from "../services.types";

export function GoalServiceMixin<TBase extends ServiceConstructor>(
  Base: TBase
) {
  return class GoalService extends Base {
    readonly goalUrl = `${process.env.REACT_APP_ENDPOINT}herds/goals`;

    async listLotGoals(
      farmId: number,
      withDeleted = true
    ): Promise<DataResult<Goal>> {
      try {
        const urlParams = this.montaURLParametros({ withDeleted });
        const url = this.montaURL(`${this.goalUrl}/${farmId}`, urlParams);
        const result = await this.get(url);

        if (result.data?.data == null) {
          return { data: [], total: 0 };
        }

        const data = result.data.data as Goal[];
        const total = result.data.total as number;

        return { data, total };
      } catch (_) {
        throw new Error(t("errors:goal.list-goals"));
      }
    }

    async createGoal(payload: Pick<Goal, "name" | "farmId">): Promise<Goal> {
      try {
        const result = await this.post(this.goalUrl, payload);
        return result.data.data as Goal;
      } catch (error) {
        throw new Error(t("errors:goal.create-goal"));
      }
    }

    async updateGoal(id: number, payload: Pick<Goal, "name">): Promise<Goal> {
      try {
        const result = await this.patch(`${this.goalUrl}/${id}`, payload);
        return result.data.data as Goal;
      } catch (error) {
        throw new Error(t("errors:goal.update-goal"));
      }
    }

    async deleteGoal(id: number): Promise<void> {
      try {
        await this.delete(`${this.goalUrl}/${id}`);
      } catch (error) {
        throw new Error(t("errors:goal.delete-goal"));
      }
    }

    async reactivateGoal(id: number): Promise<Goal> {
      try {
        const result = await this.patch(`${this.goalUrl}/${id}/reactivate`);
        return result.data.data as Goal;
      } catch (error) {
        throw new Error(t("errors:goal.reactivate-goal"));
      }
    }
  };
}
